import { useState, useRef } from "react";
import { adminApi } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import PermissionGate from "../components/PermissionGate";

type Step = "upload" | "preview" | "confirm" | "done";

export default function Backup() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [step,        setStep]        = useState<Step>("upload");
  const [backup,      setBackup]      = useState<any>(null);
  const [preview,     setPreview]     = useState<any>(null);
  const [confirmName, setConfirmName] = useState("");
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    try {
      const parsed = JSON.parse(text);
      if (!parsed.familyId || !parsed.schemaVersion) { setError("Invalid backup file."); return; }
      setBackup(parsed); setError("");
      setLoading(true);
      try {
        const res = await adminApi.backupDryRun(parsed);
        setPreview(res); setStep("preview");
      } catch { setError("Failed to run dry-run preview."); }
      finally { setLoading(false); }
    } catch { setError("Could not parse JSON file."); }
  }

  async function restore() {
    if (!backup || confirmName !== backup.family.name) return;
    setLoading(true);
    try {
      await adminApi.backupRestore(backup, confirmName);
      setStep("done");
    } catch { setError("Restore failed."); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ maxWidth: 700, display: "flex", flexDirection: "column", gap: 20 }}>
      <h1 style={{ fontSize: 20, fontWeight: 900 }}>📦 Backup & Restore</h1>

      <PermissionGate permission="backup:export"
        fallback={<div className="card" style={{ color: "var(--text-muted)" }}>backup:export permission required.</div>}>
        <div className="card" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontWeight: 700 }}>Export Family Backup</div>
          <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Enter a family ID to download a complete JSON backup.</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input id="exportFamilyId" placeholder="fam_abc123"
              style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface2)", color: "var(--text)", fontSize: 13 }} />
            <button className="btn btn-primary" onClick={async () => {
              const id = (document.getElementById("exportFamilyId") as HTMLInputElement).value.trim();
              if (!id) return;
              const res = await adminApi.exportBackup(id);
              if (!res.ok) { setError("Export failed — family not found or no permission."); return; }
              const blob = await res.blob();
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = `backup_${id}_${new Date().toISOString().slice(0,10)}.json`;
              a.click();
            }}>⬇ Export</button>
          </div>
        </div>
      </PermissionGate>

      <PermissionGate permission="backup:restore">
        <div className="card" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ fontWeight: 700 }}>Restore from Backup</div>

          {step === "upload" && (
            <>
              <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Upload a backup JSON to preview what will be restored before committing.</div>
              <input ref={fileRef} type="file" accept=".json" style={{ display: "none" }} onChange={onFile} />
              <button className="btn" onClick={() => fileRef.current?.click()} disabled={loading}>
                {loading ? "Analysing…" : "📂 Choose backup file"}
              </button>
            </>
          )}

          {step === "preview" && preview && (
            <>
              <div style={{ fontWeight: 700, color: "#7dd3fc" }}>📋 Dry Run — {preview.familyName}</div>
              <div style={{ background: "var(--surface2)", borderRadius: 8, overflow: "hidden" }}>
                {Object.entries(preview.results).map(([table, counts]: [string, any]) => (
                  <div key={table} style={{ display: "flex", gap: 10, padding: "7px 12px", borderTop: "1px solid var(--border)", fontSize: 12 }}>
                    <span style={{ fontFamily: "monospace", flex: 1, color: "var(--text-muted)" }}>{table}</span>
                    {counts.create > 0 && <span style={{ background: "rgba(34,197,94,0.15)", color: "#86efac", padding: "1px 8px", borderRadius: 5, fontSize: 10, fontWeight: 800 }}>{counts.create} CREATE</span>}
                    {counts.update > 0 && <span style={{ background: "rgba(245,158,11,0.12)", color: "#fcd34d", padding: "1px 8px", borderRadius: 5, fontSize: 10, fontWeight: 800 }}>{counts.update} UPDATE</span>}
                    {counts.skip > 0   && <span style={{ background: "rgba(255,255,255,0.06)", color: "var(--text-muted)", padding: "1px 8px", borderRadius: 5, fontSize: 10, fontWeight: 800 }}>{counts.skip} SKIP</span>}
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-primary" onClick={() => setStep("confirm")}>Continue →</button>
                <button className="btn" onClick={() => { setStep("upload"); setBackup(null); setPreview(null); }}>Cancel</button>
              </div>
            </>
          )}

          {step === "confirm" && backup && (
            <div style={{ background: "rgba(239,68,68,0.08)", border: "2px solid rgba(239,68,68,0.35)", borderRadius: 10, padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ fontWeight: 900, color: "#fca5a5" }}>⚠️ Confirm Restore</div>
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                This will overwrite <strong>{backup.family.name}'s</strong> data. Type the family name exactly to proceed.
              </div>
              <input placeholder={backup.family.name} value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(0,0,0,0.3)", color: "var(--text)", fontFamily: "monospace" }} />
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-danger" style={{ flex: 1 }} disabled={loading || confirmName !== backup.family.name} onClick={restore}>
                  {loading ? "Restoring…" : "Restore Family Data"}
                </button>
                <button className="btn" onClick={() => setStep("preview")}>← Back</button>
              </div>
            </div>
          )}

          {step === "done" && (
            <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 10, padding: 16, textAlign: "center" }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>✅</div>
              <div style={{ fontWeight: 800, color: "#86efac" }}>Restore complete</div>
              <button className="btn" style={{ marginTop: 12 }} onClick={() => { setStep("upload"); setBackup(null); setPreview(null); setConfirmName(""); }}>Restore another</button>
            </div>
          )}

          {error && <div style={{ color: "#f87171", fontSize: 13 }}>{error}</div>}
        </div>
      </PermissionGate>
    </div>
  );
}
